import type { Employee } from './useEmployees';

/** Nur das, was der Abgleich braucht — nicht der ganze AdminUser. */
export interface AbgleichKonto {
  email: string | null;
  zoho_user_id: string | null;
}

export interface AbgleichErgebnis {
  /** In People, aber ohne Hub-Konto — hier wird etwas angelegt. */
  ohneZugang: Employee[];
  /** In People und im Hub — nur als Zahl interessant. */
  verknuepft: number;
  /** Ausgeblendet, weil in People nicht mehr aktiv und ohne Hub-Konto. */
  ausgetreten: number;
}

/**
 * Stellt den Personalstamm den Hub-Konten gegenüber.
 *
 * **Zwei Wege, ein Konto wiederzuerkennen**, und beide sind nötig:
 *
 * - über `zoho_user_id` — sicher, aber nur bei Konten vorhanden, die über
 *   diesen Weg angelegt wurden. Alle Bestandskonten haben die Spalte leer.
 * - über die E-Mail-Adresse — die einzige Brücke zu den Bestandskonten, aber
 *   nur so gut wie die Pflege auf beiden Seiten.
 *
 * Wer über einen der beiden Wege gefunden wird, gilt als versorgt. Lieber
 * jemanden übersehen, der schon einen Zugang hat, als ein zweites Konto für
 * dieselbe Person anzulegen: ein doppeltes Konto merkt niemand sofort, und
 * beim Deaktivieren erwischt man dann das falsche.
 *
 * **Ausgetretene ohne Hub-Konto werden ausgeblendet** — für sie einen Zugang
 * anzulegen wäre offensichtlich falsch. Ausgetretene *mit* Konto erscheinen
 * hier nicht, weil sie als verknüpft gelten; dass deren Zugang noch offen ist,
 * ist eine eigene Frage und nicht die dieser Ansicht.
 */
export function abgleichen(employees: Employee[], konten: AbgleichKonto[]): AbgleichErgebnis {
  const mails = new Set<string>();
  const zohoIds = new Set<string>();
  for (const k of konten) {
    if (k.email) mails.add(k.email.trim().toLowerCase());
    if (k.zoho_user_id) zohoIds.add(k.zoho_user_id);
  }

  const ohneZugang: Employee[] = [];
  let verknuepft = 0;
  let ausgetreten = 0;

  for (const e of employees) {
    const hatKonto = zohoIds.has(e.id) || (e.email !== null && mails.has(e.email));
    if (hatKonto) {
      verknuepft++;
      continue;
    }
    if (!e.aktiv) {
      ausgetreten++;
      continue;
    }
    ohneZugang.push(e);
  }

  return { ohneZugang, verknuepft, ausgetreten };
}
